const API_BASE = process.env.REACT_APP_API_URL || "";
const MUTE_STORAGE_KEY = "game_sounds_muted";

let bgAudio = null;
let muted = false;
let audioChannel = "user";
let userChannelMuted = false;
let adminChannelMuted = false;
const listeners = new Set();
const channelMuteListeners = new Set();
const activeAudios = new Set();

const readMutedFromStorage = () => {
  try {
    return localStorage.getItem(MUTE_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

muted = readMutedFromStorage();

const notifyListeners = () => {
  listeners.forEach((listener) => {
    try {
      listener(muted);
    } catch {
      /* ignore */
    }
  });
};

const getRemoteChannelMutesSnapshot = () => ({
  userMuted: userChannelMuted,
  adminMuted: adminChannelMuted,
});

const notifyChannelMuteListeners = () => {
  const snapshot = getRemoteChannelMutesSnapshot();
  channelMuteListeners.forEach((listener) => {
    try {
      listener(snapshot);
    } catch {
      /* ignore */
    }
  });
};

export const isChannelMuted = () =>
  audioChannel === "admin" ? adminChannelMuted : userChannelMuted;

export const isEffectivelyMuted = () => muted || isChannelMuted();

export const isGameSoundMuted = () => muted;

export const getAudioChannel = () => audioChannel;

export const setAudioChannel = (channel) => {
  const next = channel === "admin" ? "admin" : "user";
  if (next === audioChannel) {
    if (isEffectivelyMuted()) stopAllGameAudio();
    return;
  }
  audioChannel = next;
  if (isEffectivelyMuted()) stopAllGameAudio();
};

export const getRemoteChannelMutes = () => getRemoteChannelMutesSnapshot();

export const setRemoteChannelMutes = ({ userMuted, adminMuted } = {}) => {
  const nextUser = !!userMuted;
  const nextAdmin = !!adminMuted;
  if (nextUser === userChannelMuted && nextAdmin === adminChannelMuted) {
    if (isEffectivelyMuted()) stopAllGameAudio();
    return;
  }
  userChannelMuted = nextUser;
  adminChannelMuted = nextAdmin;
  if (isEffectivelyMuted()) stopAllGameAudio();
  notifyChannelMuteListeners();
};

export const subscribeChannelMute = (listener) => {
  channelMuteListeners.add(listener);
  try {
    listener(getRemoteChannelMutesSnapshot());
  } catch {
    /* ignore */
  }
  return () => channelMuteListeners.delete(listener);
};

export const setGameSoundMuted = (nextMuted) => {
  const value = !!nextMuted;
  if (value === muted) return;
  muted = value;
  try {
    localStorage.setItem(MUTE_STORAGE_KEY, value ? "1" : "0");
  } catch {
    /* ignore */
  }
  if (isEffectivelyMuted()) stopAllGameAudio();
  notifyListeners();
};

export const toggleGameSoundMuted = () => {
  setGameSoundMuted(!muted);
};

export const subscribeGameSoundMute = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const resolveAudioUrl = (url) => {
  if (!url || typeof url !== "string") return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `${API_BASE}${trimmed.startsWith("/") ? trimmed : `/${trimmed}`}`;
};

const registerAudio = (audio) => {
  if (!audio) return audio;
  activeAudios.add(audio);
  const cleanup = () => activeAudios.delete(audio);
  audio.addEventListener("ended", cleanup);
  audio.addEventListener("error", cleanup);
  return audio;
};

export const playSound = (url, { volume = 1, loop = false, ignoreMute = false } = {}) => {
  if (!ignoreMute && isEffectivelyMuted()) return null;
  const src = resolveAudioUrl(url);
  if (!src) return null;
  try {
    const audio = new Audio(src);
    audio.volume = Math.min(1, Math.max(0, Number(volume) || 0));
    audio.loop = !!loop;
    audio.play().catch(() => {});
    return registerAudio(audio);
  } catch {
    return null;
  }
};

export const stopSound = (audio) => {
  if (!audio) return;
  try {
    audio.pause();
    audio.currentTime = 0;
  } catch {
    /* ignore */
  }
  activeAudios.delete(audio);
};

const fittedDurationCache = new Map();

/** Warm the clip length so the next fitted play can start in sync, without a metadata wait. */
export const primeAudioDuration = (url) => {
  const src = resolveAudioUrl(url);
  if (!src || fittedDurationCache.has(src)) return;
  try {
    const audio = new Audio();
    audio.preload = "auto";
    audio.addEventListener("loadedmetadata", () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        fittedDurationCache.set(src, audio.duration);
      }
    }, { once: true });
    audio.src = src;
  } catch {
    /* ignore */
  }
};

/**
 * Play a one-shot stretched or squeezed so it lasts durationMs.
 * Used to keep the prize-wheel spin track lined up with the suspense spin.
 */
export const playSoundFitted = (url, { volume = 1, durationMs = 1000, ignoreMute = false } = {}) => {
  if (!ignoreMute && isEffectivelyMuted()) return null;
  const src = resolveAudioUrl(url);
  if (!src) return null;
  try {
    const audio = new Audio(src);
    audio.volume = Math.min(1, Math.max(0, Number(volume) || 0));
    audio.loop = false;
    audio.preservesPitch = true;
    try { audio.webkitPreservesPitch = true; } catch { /* ignore */ }
    const target = Math.max(0.3, (Number(durationMs) || 0) / 1000);
    const applyAndPlay = (length) => {
      if (length > 0 && Number.isFinite(length)) {
        const rate = length / target;
        audio.playbackRate = Math.min(4, Math.max(0.25, rate));
      }
      audio.play().catch(() => {});
    };
    const known = fittedDurationCache.get(src);
    if (known) {
      applyAndPlay(known);
    } else {
      const onMeta = () => {
        if (Number.isFinite(audio.duration) && audio.duration > 0) {
          fittedDurationCache.set(src, audio.duration);
        }
        applyAndPlay(audio.duration);
      };
      if (audio.readyState >= 1 && audio.duration) onMeta();
      else audio.addEventListener("loadedmetadata", onMeta, { once: true });
    }
    return registerAudio(audio);
  } catch {
    return null;
  }
};

/** Pause the current loop without dropping it. The next loop start replaces it. */
export const pauseBgMusic = () => {
  if (!bgAudio) return;
  try {
    bgAudio.pause();
  } catch {
    /* ignore */
  }
};

export const resumeBgMusic = () => {
  if (!bgAudio || isEffectivelyMuted() || !bgAudio.paused) return;
  bgAudio.play().catch(() => {});
};

export const stopBgMusic = () => {
  if (bgAudio) {
    try {
      bgAudio.pause();
      bgAudio.currentTime = 0;
    } catch {
      /* ignore */
    }
    activeAudios.delete(bgAudio);
    bgAudio = null;
  }
};

export const stopAllGameAudio = () => {
  stopBgMusic();
  activeAudios.forEach((audio) => {
    try {
      audio.pause();
      audio.currentTime = 0;
    } catch {
      /* ignore */
    }
  });
  activeAudios.clear();
};

export const startBgMusic = (url, { volume = 0.4, ignoreMute = false } = {}) => {
  if (!ignoreMute && isEffectivelyMuted()) return null;
  stopBgMusic();
  const audio = playSound(url, { volume, loop: true, ignoreMute });
  if (audio) bgAudio = audio;
  return audio;
};
